<template>
  <div class="layout-panel-wrapper">
    <a-alert v-if="configError" type="error" show-icon :message="configError">
      <template #action
        ><a-button
          size="small"
          :loading="configLoading"
          @click="loadPublishedConfig"
          >重试加载</a-button
        ></template
      >
    </a-alert>
    <a-alert
      v-if="baselineError"
      type="warning"
      show-icon
      :message="baselineError"
      description="切换发布模式前需要成功获取对应的版本基准，现有草稿会保留。"
    >
      <template #action
        ><a-button
          size="small"
          :loading="baselineLoading"
          @click="loadOtherVersion"
          >重试获取版本基准</a-button
        ></template
      >
    </a-alert>
    <div class="layout-panel">
      <aside class="preview-column">
        <section class="panel-card preview-card">
          <header class="panel-header">
            <div class="preview-title-row">
              <strong>App 端预览</strong>
              <span v-if="currentVersion" class="version-badge">
                <i class="version-status-dot"></i>
                线上&nbsp;&nbsp;{{ currentVersion }}
              </span>
            </div>
          </header>
          <div class="phone-shell">
            <div class="phone-speaker"></div>
            <div class="phone-content"></div>
            <div class="phone-tabs">
              <div
                v-for="item in items"
                :key="item.materialNo"
                class="phone-tab"
                :class="{ active: item.materialNo === previewMaterialNo }"
                role="button"
                tabindex="0"
                @click="handlePreviewSelect(item)"
                @keydown.enter.space.prevent="handlePreviewSelect(item)"
              >
                <span class="phone-icon">
                  <img
                    v-if="
                      getTabIconUrl(item, item.materialNo === previewMaterialNo)
                    "
                    :src="
                      getTabIconUrl(item, item.materialNo === previewMaterialNo)
                    "
                    :alt="getMaterialName(item)"
                  />
                  <PictureOutlined v-else />
                </span>
                <span
                  v-if="Number(item.materialType) === 3"
                  class="phone-tab-text"
                >
                  {{ getMaterialDisplayText(item) }}
                </span>
              </div>
            </div>
          </div>
          <p class="preview-tip">点击 Tab 可预览选中态，不影响默认选中配置</p>
        </section>

        <section class="panel-card check-card">
          <header class="panel-header">
            <strong>发布前校验</strong>
            <span>{{
              draft.experimentEnabled
                ? '以下为当前变体；发布时校验全部变体'
                : '全部通过才能发布'
            }}</span>
          </header>
          <ul>
            <li
              v-for="item in publishChecks"
              :key="item.text"
              :class="{ invalid: !item.valid }"
            >
              <CheckCircleFilled v-if="item.valid" />
              <ExclamationCircleOutlined v-else />
              <span>{{ item.text }}</span>
            </li>
          </ul>
        </section>
      </aside>

      <section class="panel-card tab-list-card">
        <ExperimentConfigPanel
          ref="experimentPanelRef"
          :draft="draft"
          :active-variant-id="activeVariantId"
          :disabled="interactionDisabled"
          :error="validationError"
          @toggle="handleExperimentToggle"
          @field-change="handleExperimentField"
          @add="openVariantModal()"
          @select="selectVariant"
          @edit="openVariantModal"
          @remove="removeVariant"
        />
        <header class="panel-header list-header">
          <div>
            <strong>栏位列表</strong>
            <span>{{ items.length }} / 5 个栏位 · 按住左侧手柄拖拽排序</span>
          </div>
          <div>
            <a-button
              type="primary"
              :loading="candidateLoading"
              :disabled="interactionDisabled || !remainingSlots"
              @click="openCandidateModal"
            >
              + 添加 Tab
            </a-button>
          </div>
        </header>

        <draggable
          v-model="items"
          :disabled="interactionDisabled"
          item-key="materialNo"
          handle=".drag-handle"
          ghost-class="drag-ghost"
          chosen-class="drag-chosen"
        >
          <template #item="{ element: item, index }">
            <div class="tab-row">
              <span class="drag-handle" title="拖拽排序">
                <HolderOutlined />
              </span>
              <span class="sort-index">{{ index + 1 }}</span>
              <span class="tab-icon">
                <img
                  v-if="getTabIconUrl(item)"
                  :src="getTabIconUrl(item)"
                  :alt="getMaterialName(item)"
                />
                <PictureOutlined v-else />
              </span>
              <div class="tab-info">
                <strong>{{ getMaterialName(item) }}</strong>
                <span>{{ item.materialNo }} · {{ getJumpTarget(item) }}</span>
              </div>
              <a-tag>{{ item.materialTypeName }}</a-tag>
              <a-radio
                :disabled="interactionDisabled"
                :checked="item.materialNo === defaultMaterialNo"
                @click="handleSetDefault(item)"
              >
                默认选中
              </a-radio>
              <a-button
                type="link"
                :disabled="interactionDisabled"
                @click="openEditModal(item)"
              >
                编辑
              </a-button>
              <a-button
                type="link"
                danger
                :disabled="interactionDisabled"
                @click="handleRemove(item)"
              >
                移除
              </a-button>
            </div>
          </template>
        </draggable>
        <a-empty
          v-if="!items.length"
          class="empty-tabs"
          :description="configLoading ? '正在加载配置' : '暂无 Tab，请添加物料'"
        />
      </section>
    </div>

    <div class="publish-action-bar">
      <a-button
        type="primary"
        :loading="publishLoading"
        :disabled="interactionDisabled"
        @click="handleOpenPublish"
      >
        发布
      </a-button>
    </div>

    <AddTabCandidatesModal
      v-model:visible="candidateModalVisible"
      :candidates="candidateItems"
      :max-selectable="remainingSlots"
      :experiment="!!draft.experimentEnabled"
      :default-language-code="defaultLanguageCode"
      @confirm="handleAddCandidates"
    />

    <PublishConfigModal
      v-model:visible="publishModalVisible"
      :changes="publishChanges"
      :confirm-loading="publishLoading"
      @confirm="handlePublish"
    />

    <TabFormModal
      v-model:visible="modalVisible"
      mode="edit"
      :record="editingRecord"
      :languages="languageOptions"
      :confirm-loading="submitLoading"
      @submit="handleSubmit"
    />
    <VariantFormModal
      v-model:visible="variantModalVisible"
      :record="editingVariant"
      :variants="draft.variants"
      @confirm="handleVariantConfirm"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import {
  CheckCircleFilled,
  ExclamationCircleOutlined,
  HolderOutlined,
  PictureOutlined
} from '@ant-design/icons-vue'
import useTabConfiguration from '../useTabConfiguration'
import AddTabCandidatesModal from './AddTabCandidatesModal.vue'
import ExperimentConfigPanel from './ExperimentConfigPanel.vue'
import PublishConfigModal from './PublishConfigModal.vue'
import TabFormModal from './TabFormModal.vue'
import VariantFormModal from './VariantFormModal.vue'

export default {
  components: {
    AddTabCandidatesModal,
    CheckCircleFilled,
    ExclamationCircleOutlined,
    draggable,
    ExperimentConfigPanel,
    HolderOutlined,
    PictureOutlined,
    PublishConfigModal,
    TabFormModal,
    VariantFormModal
  },
  props: {
    siteId: { type: [Number, String], default: undefined }
  },
  setup(props) {
    const getJumpTarget = item =>
      Number(item.jumpType) === 1
        ? item.h5Url || '-'
        : `路由 · ${item.routeName || item.routeUrl || item.routeId || '-'}`
    const getTabIconUrl = (item, selected = false) =>
      selected
        ? item.selectedIconUrl || item.unselectedIconUrl || ''
        : item.unselectedIconUrl || item.selectedIconUrl || ''
    return { ...useTabConfiguration(props), getJumpTarget, getTabIconUrl }
  }
}
</script>

<style lang="scss" scoped>
.layout-panel-wrapper {
  display: flex;
  height: 100%;
  min-height: 0;
  min-width: 960px;
  flex-direction: column;
}

.layout-panel {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(360px, 0.55fr) minmax(580px, 1fr);
  gap: 20px;
  align-items: start;
  min-height: 0;
  overflow: auto;
}

.preview-column {
  display: grid;
  gap: 20px;
}

.panel-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}

.panel-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;

  strong {
    color: #262626;
    font-size: 15px;
  }

  span {
    color: #8c8c8c;
    font-size: 12px;
  }
}

.preview-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.version-badge {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fff;
  color: #595959;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  gap: 8px;
}

.version-status-dot {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #52c41a;
  box-shadow: 0 0 0 4px rgba(82, 196, 26, 0.12);
}

.list-header {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}

.preview-card {
  padding-bottom: 18px;
}

.phone-shell {
  width: 300px;
  height: 400px;
  margin: 22px auto 0;
  overflow: hidden;
  border: 1px solid #262626;
  border-radius: 30px;
  background: #f7f5f1;
  position: relative;
}

.phone-speaker {
  position: absolute;
  top: 10px;
  left: 50%;
  width: 70px;
  height: 8px;
  border-radius: 8px;
  background: #595959;
  transform: translateX(-50%);
}

.phone-content {
  height: 340px;
}

.phone-tabs {
  display: flex;
  height: 60px;
  border-top: 1px solid #d9d9d9;
  background: #fff;
}

.phone-tab {
  display: flex;
  min-width: 0;
  padding-top: 10px;
  flex: 1 1 0;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
  color: #595959;
  cursor: pointer;
  font-size: 10px;
  user-select: none;

  &.active {
    color: #cf3e0d;
    background: #fff7f0;
  }
}

.phone-icon {
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.phone-tab-text {
  width: 100%;
  text-align: center;
}

.preview-tip {
  margin: 16px 0 0;
  color: #8c8c8c;
  text-align: center;
  font-size: 12px;
}

.check-card ul {
  margin: 0;
  padding: 14px 16px;
  list-style: none;
}

.check-card li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  color: #595959;

  :deep(.anticon) {
    color: #52c41a;
  }
  &.invalid :deep(.anticon) {
    color: #faad14;
  }
}

.empty-tabs {
  padding: 24px 0;
}

.tab-list-card {
  overflow: hidden;
}

.tab-row {
  display: grid;
  grid-template-columns: 30px 24px 44px minmax(140px, 1fr) 90px 105px 48px 48px;
  align-items: center;
  min-height: 68px;
  padding: 0 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: 0;
  }
}

.drag-handle {
  color: #8c8c8c;
  cursor: grab;
  font-size: 18px;

  &:active {
    cursor: grabbing;
  }
}

.drag-ghost {
  background: #e6f7ff;
  opacity: 0.6;
}

.drag-chosen {
  box-shadow: inset 3px 0 0 #1890ff;
}

.sort-index {
  color: #595959;
  font-weight: 600;
}

.tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
  font-size: 20px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.tab-info {
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    color: #262626;
  }

  span {
    color: #8c8c8c;
    font-size: 12px;
  }
}

.publish-action-bar {
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  flex: 0 0 auto;
  min-height: 72px;
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  align-items: center;
  justify-content: flex-end;

  .ant-btn {
    min-width: 88px;
    height: 36px;
    font-weight: 500;
  }
}
</style>
